import { gql } from '@apollo/client'
import type { LoanStatus, TransactionType } from '@phoenix-web/utils/journey'

type ClientPerson = {
  name: string
  email: string
  phone: string
}

export type ClientDetail = {
  leadId: string
  status: LoanStatus
  departingPropertyAddress1: string
  departingPropertyAddress2: string
  eligibilityDetails: {
    fairMarketValue: number
    lastRunDate: string
    rejectionMessaging: string[]
    includeBridgePrequalify: boolean
    contractFeeInfo?: {
      contractFeePromotion?: string | null
      contractFeePercent: number | null
      contractFeeMax: number | null
      contractFeeMaxReached: boolean | null
      contractFeeElp: number | null
    }
    bridgeLoan: {
      bridgeLoanPassed: boolean | null
      deductions: {
        sixMonthPiti: number | null
        contractFee: number | null
        closingCosts: number | null
      }
      newHomeFundsAvailable: number | null
      maxBridgeLoan: number | null
    }
    bridgeLoanPlus: {
      bridgeLoanPlusPassed: boolean | null
      deductions: {
        mortgageBalancePayoff: number | null
        contractFee: number | null
        closingCosts: number | null
        loanOriginationFeePercent: number | null
        loanOriginationFee: number | null
      }
      newHomeFundsAvailable: number | null
      maxBridgeLoan: number | null
    }
    disclaimers?: Array<{ marker: string; text: string }>
  }
  loanDetails: {
    loanId: string
    status: string
    loanAmount: string
    preapprovalDate: string
    estimatedClosingDate: string
    purchaseContractClosingDate: string
    borrowerDocsDueByDate: string
    bridgeWiredFundsAmount: string
    disbursementDate: string
    rescissionDate: string
    fundsReleasedDate: string
    maturityDate: string
    hasOnlyAdversedLoan: boolean
  }
  transactionDetails: {
    transactionId: string
    transactionType: TransactionType
  }
  borrower: ClientPerson
  coborrower?: ClientPerson
  listingAgent?: ClientPerson
}

export type ClientDetailResponse = {
  clientDetail: ClientDetail
}

export type BackyardClientDetailResponse = {
  backyardClientDetail: ClientDetail
}

export const FRAGMENT_CLIENT_DETAIL = gql`
  fragment FragmentClientDetail on ClientDetailResponse {
    status
    leadId: lead_id
    departingPropertyAddress1: departing_property_address_1
    departingPropertyAddress2: departing_property_address_2
    eligibilityDetails: eligibility_details {
      fairMarketValue: fair_market_value
      lastRunDate: last_run_date
      rejectionMessaging: rejection_messaging
      includeBridgePrequalify: include_bridge_prequalify
      contractFeeInfo: contract_fee_info {
        contractFeePromotion: contract_fee_promotion
        contractFeePercent: contract_fee_percent
        contractFeeMax: contract_fee_max
        contractFeeMaxReached: contract_fee_max_reached
        contractFeeElp: contract_fee_elp
      }
      bridgeLoan: bridge_loan {
        bridgeLoanPassed: bridge_loan_passed
        deductions: deductions {
          sixMonthPiti: six_month_piti
          contractFee: contract_fee
          closingCosts: closing_costs
        }
        newHomeFundsAvailable: new_home_funds_available
        maxBridgeLoan: max_bridge_loan
      }
      bridgeLoanPlus: bridge_loan_plus {
        bridgeLoanPlusPassed: bridge_loan_plus_passed
        deductions: deductions {
          mortgageBalancePayoff: mortgage_balance_payoff
          contractFee: contract_fee
          closingCosts: closing_costs
          loanOriginationFeePercent: loan_origination_fee_percent
          loanOriginationFee: loan_origination_fee
        }
        newHomeFundsAvailable: new_home_funds_available
        maxBridgeLoan: max_bridge_loan
      }
      disclaimers {
        marker
        text
      }
    }
    loanDetails: loan_details {
      loanId: id
      status
      loanAmount: loan_amount
      preapprovalDate: preapproval_date
      estimatedClosingDate: estimated_closing_date
      purchaseContractClosingDate: purchase_contract_closing_date
      borrowerDocsDueByDate: borrower_docs_due_by_date
      bridgeWiredFundsAmount: bridge_wired_funds_amount
      disbursementDate: disbursement_date
      rescissionDate: rescission_date
      fundsReleasedDate: funds_released_date
      maturityDate: maturity_date
      hasOnlyAdversedLoan: has_only_adversed_loan
    }
    transactionDetails: transaction_details {
      transactionId: transaction_id
      transactionType: transaction_type
    }
    borrower {
      name
      email
      phone
    }
    coborrower {
      name
      email
      phone
    }
    listingAgent: listing_agent {
      name
      email
      phone
    }
  }
`

export const CLIENT_DETAIL = gql`
  query clientDetail($input: ClientDetailInput!) {
    clientDetail(input: $input) {
      ...FragmentClientDetail
    }
  }
  ${FRAGMENT_CLIENT_DETAIL}
`

export const BACKYARD_CLIENT_DETAIL = gql`
  query backyardClientDetail($input: BackyardClientDetailInput!) {
    backyardClientDetail(input: $input) {
      ...FragmentClientDetail
    }
  }
  ${FRAGMENT_CLIENT_DETAIL}
`
